import * as THREE from "three";
import { IVfxTask } from "../../core/runtime/vfx_task_runner.js";
import { IController, StopBehavior } from "../../core/types/controller_types.js";
import { Vfx3DRuntime } from "../../vfx_3d_runtime.js";
import { randomRange } from "../../../common/common_funcs.js";
import { ResourceID } from "../../../resource_loader/resource_id.js";
import { VfxPhysicsParticleBatch } from "../../core/batches/vfx_physics_particle_batch.js";
import { GLASS_SHARD_IDS } from "./glass_shard_texture_builder.js";

export class VfxJamSplashTask implements IVfxTask, IController
{
  // --- Common Spawn Geometry ---
  private static readonly HEIGHT_SPAN_FACTOR         : number = 0.75;
  private static readonly HEIGHT_MULT_BASE           : number = 1.0;
  private static readonly HEIGHT_MULT_STEP           : number = 0.4;

  // --- Jam Splash Constants ---
  private static readonly JAM_COUNT                  : number = 25;
  private static readonly JAM_LIFE_SPAN              : number = 0.8;
  private static readonly JAM_LIFESPAN_MIN_MULT      : number = 0.8;
  private static readonly JAM_LIFESPAN_MAX_MULT      : number = 1;
  private static readonly JAM_RADIAL_MIN             : number = 1.5;
  private static readonly JAM_RADIAL_MAX             : number = 4;
  private static readonly JAM_UP_MIN                 : number = 0.5;
  private static readonly JAM_UP_MAX                 : number = 3.5;
  private static readonly JAM_GRAVITY                : number = 14.0;
  private static readonly JAM_SCALE_MIN              : number = 0.7;
  private static readonly JAM_SCALE_MAX              : number = 0.8;

  private static readonly JAM_SURFACE_MAP: Record<number, ResourceID> = {
    0: ResourceID.JamCherryTexture,
    1: ResourceID.JamPlumTexture,
    2: ResourceID.JamLemonTexture,
    3: ResourceID.JamFigTexture,
  };
  private static readonly JAM_DEFAULT_SURFACE        : ResourceID = ResourceID.JamCherryTexture;
  private static readonly JAM_NOISE_RES_ID           : ResourceID = ResourceID.PerlinNoiseTexture;
  private static readonly JAM_NOISE_SCALE            : number = 2;
  private static readonly JAM_DISSOLVE_START         : number = 0;
  private static readonly JAM_DISSOLVE_SOFTNESS      : number = 0.1;

  // --- Glass Shard Constants ---
  private static readonly GLASS_ATLAS_COLUMNS        : number = 4;
  private static readonly GLASS_COUNT                : number = 15;
  private static readonly GLASS_LIFE_SPAN            : number = 1.0;
  private static readonly GLASS_LIFESPAN_MIN_MULT    : number = 0.8;
  private static readonly GLASS_LIFESPAN_MAX_MULT    : number = 1.0;
  private static readonly GLASS_RADIAL_MIN           : number = 2.0;
  private static readonly GLASS_RADIAL_MAX           : number = 3.0;
  private static readonly GLASS_UP_MIN               : number = 1.0;
  private static readonly GLASS_UP_MAX               : number = 3.5;
  private static readonly GLASS_GRAVITY              : number = 12.0;
  private static readonly GLASS_SCALE_MIN            : number = 0.3;
  private static readonly GLASS_SCALE_MAX            : number = 0.4;
  private static readonly GLASS_OFFSET_XZ_SPAN_FACTOR: number = 0.5;
  private static readonly GLASS_SPIN_VEL_THRESHOLD   : number = 0.1;
  private static readonly GLASS_SPIN_SPEED_MIN       : number = 5.0;
  private static readonly GLASS_SPIN_SPEED_FACTOR    : number = 4.0;
  private static readonly GLASS_FADE_START           : number = 0.6;
  private static readonly GLASS_SHRINK_START         : number = 1.0;
  private static readonly GLASS_FLASH_INTENSITY      : number = 1.8;
  private static readonly GLASS_RESTITUTION          : number = 0.25;
  private static readonly GLASS_DEFAULT_RES_ID       : ResourceID = ResourceID.GlassShard1;

  public is_active: boolean = true;
  private position: THREE.Vector3;
  private half_height: number;
  private height_blocks: number;
  private jam_set_index: number;
  private splash_force_factor: number;
  private glass_force_factor: number;
  private rotation: THREE.Quaternion;
  private floor_y: number;
  private start_time: number;
  private has_spawned: boolean = false;

  constructor(
    position: THREE.Vector3,
    half_height: number,
    height_blocks: number,
    jam_set_index: number,
    splash_force_factor: number = 1,
    glass_force_factor: number = 1,
    floor_y: number = 0,
    rotation?: THREE.Quaternion,
  )
  {
    this.position = position.clone();
    this.half_height = half_height;
    this.height_blocks = height_blocks;
    this.jam_set_index = jam_set_index;
    this.splash_force_factor = splash_force_factor;
    this.glass_force_factor = glass_force_factor;
    this.floor_y = floor_y;
    this.rotation = rotation ? rotation.clone() : new THREE.Quaternion();
    this.start_time = Vfx3DRuntime.current_time;
  }

  public setPosition( pos: THREE.Vector3 ): void
  {
    this.position.copy( pos );
  }

  public stop( _behavior?: StopBehavior ): void
  {
    this.is_active = false;
  }

  public start(): void
  {
    this.is_active = true;
  }

  public accelerate( _factor: number ): void {}

  private static readonly scratch_pos: THREE.Vector3 = new THREE.Vector3();
  private static readonly scratch_vel: THREE.Vector3 = new THREE.Vector3();
  private static readonly scratch_offset: THREE.Vector3 = new THREE.Vector3();

  public onUpdate( now: number ): boolean
  {
    if ( !this.is_active )
      return true;
    if ( now < this.start_time )
      return false;

    const age = now - this.start_time;

    if ( !this.has_spawned )
    {
      this.has_spawned = true;

      const span = this.half_height * VfxJamSplashTask.HEIGHT_SPAN_FACTOR;
      const mult = VfxJamSplashTask.HEIGHT_MULT_BASE + VfxJamSplashTask.HEIGHT_MULT_STEP * ( this.height_blocks - 1 );

      // 1. Spawn liquid jam particles
      const jam_batch = Vfx3DRuntime.getBatch( VfxPhysicsParticleBatch );
      const jam_count = Math.round( VfxJamSplashTask.JAM_COUNT * mult * this.splash_force_factor );

      const surface_res_id = VfxJamSplashTask.JAM_SURFACE_MAP[ this.jam_set_index ] ?? VfxJamSplashTask.JAM_DEFAULT_SURFACE;

      for ( let i = 0; i < jam_count; i++ )
      {
        const angle = randomRange( 0, Math.PI * 2 );
        const rs    = randomRange( VfxJamSplashTask.JAM_RADIAL_MIN, VfxJamSplashTask.JAM_RADIAL_MAX );
        VfxJamSplashTask.scratch_offset.set( 0, randomRange( -span, span ), 0 ).applyQuaternion( this.rotation );
        VfxJamSplashTask.scratch_pos.copy( this.position ).add( VfxJamSplashTask.scratch_offset );
        const local_vx = Math.cos( angle ) * rs;
        const local_vz = Math.sin( angle ) * rs;
        const local_vy = randomRange( VfxJamSplashTask.JAM_UP_MIN, VfxJamSplashTask.JAM_UP_MAX ) * this.splash_force_factor;
        VfxJamSplashTask.scratch_vel.set( local_vx, local_vy, local_vz ).applyQuaternion( this.rotation );
        const scale = randomRange( VfxJamSplashTask.JAM_SCALE_MIN, VfxJamSplashTask.JAM_SCALE_MAX );

        jam_batch.spawnParticle(
          surface_res_id,
          VfxJamSplashTask.JAM_NOISE_RES_ID,
          VfxJamSplashTask.scratch_pos,
          VfxJamSplashTask.scratch_vel,
          VfxJamSplashTask.JAM_GRAVITY,
          scale,
          this.start_time,
          VfxJamSplashTask.JAM_LIFE_SPAN * randomRange(
            VfxJamSplashTask.JAM_LIFESPAN_MIN_MULT,
            VfxJamSplashTask.JAM_LIFESPAN_MAX_MULT
          ),
          0,
          VfxJamSplashTask.JAM_NOISE_SCALE,
          undefined,
          undefined,
          undefined,
          VfxJamSplashTask.JAM_DISSOLVE_START,
          VfxJamSplashTask.JAM_DISSOLVE_SOFTNESS
        );
      }

      // 2. Spawn glass shard particles
      const shard_batch = Vfx3DRuntime.getBatch( VfxPhysicsParticleBatch );
      const shard_count = Math.round( VfxJamSplashTask.GLASS_COUNT * mult * this.glass_force_factor );

      for ( let i = 0; i < shard_count; i++ )
      {
        const angle = randomRange( 0, Math.PI * 2 );
        const rs    = randomRange( VfxJamSplashTask.GLASS_RADIAL_MIN, VfxJamSplashTask.GLASS_RADIAL_MAX ) * this.glass_force_factor;
        const xz_span = span * VfxJamSplashTask.GLASS_OFFSET_XZ_SPAN_FACTOR;
        VfxJamSplashTask.scratch_offset.set(
          randomRange( -xz_span, xz_span ),
          randomRange( -span, span ),
          randomRange( -xz_span, xz_span )
        ).applyQuaternion( this.rotation );
        VfxJamSplashTask.scratch_pos.copy( this.position ).add( VfxJamSplashTask.scratch_offset );
        const local_vx = Math.cos( angle ) * rs;
        const local_vz = Math.sin( angle ) * rs;
        const local_vy = randomRange( VfxJamSplashTask.GLASS_UP_MIN, VfxJamSplashTask.GLASS_UP_MAX ) * this.glass_force_factor;
        VfxJamSplashTask.scratch_vel.set( local_vx, local_vy, local_vz ).applyQuaternion( this.rotation );
        const scale = randomRange( VfxJamSplashTask.GLASS_SCALE_MIN, VfxJamSplashTask.GLASS_SCALE_MAX );

        const spin_dir = Math.abs( VfxJamSplashTask.scratch_vel.x ) > VfxJamSplashTask.GLASS_SPIN_VEL_THRESHOLD
          ? ( VfxJamSplashTask.scratch_vel.x >= 0 ? 1 : -1 )
          : ( Math.random() < 0.5 ? 1 : -1 );
        const spin_speed = Math.max(
          VfxJamSplashTask.GLASS_SPIN_SPEED_MIN,
          Math.abs( VfxJamSplashTask.scratch_vel.x * VfxJamSplashTask.GLASS_SPIN_SPEED_FACTOR )
        );

        const tile_idx = Math.floor( randomRange( 0, VfxJamSplashTask.GLASS_ATLAS_COLUMNS ) );
        const res_id = GLASS_SHARD_IDS[ tile_idx % GLASS_SHARD_IDS.length ] ?? VfxJamSplashTask.GLASS_DEFAULT_RES_ID;

        shard_batch.spawnShard(
          res_id,
          VfxJamSplashTask.scratch_pos,
          VfxJamSplashTask.scratch_vel,
          VfxJamSplashTask.GLASS_GRAVITY,
          scale,
          randomRange( 0, Math.PI * 2 ),
          spin_dir * spin_speed,
          this.start_time,
          VfxJamSplashTask.GLASS_LIFE_SPAN * randomRange(
            VfxJamSplashTask.GLASS_LIFESPAN_MIN_MULT,
            VfxJamSplashTask.GLASS_LIFESPAN_MAX_MULT
          ),
          this.floor_y,
          VfxJamSplashTask.GLASS_FADE_START,
          VfxJamSplashTask.GLASS_SHRINK_START,
          VfxJamSplashTask.GLASS_FLASH_INTENSITY,
          VfxJamSplashTask.GLASS_RESTITUTION
        );
      }
    }

    if ( age > Math.max( VfxJamSplashTask.JAM_LIFE_SPAN, VfxJamSplashTask.GLASS_LIFE_SPAN ) )
    {
      return true;
    }

    return false;
  }
}
